import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Switch } from '@headlessui/react';
import { ChevronLeft, ChevronRight, ArrowRight, Loader2, CheckCircle, AlertCircle, Clock, Database, Shield, Bot, Layers, LayoutGrid, CornerUpLeft, Target, Zap } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { getTranslatedSeverityLabel } from '../../../../utils/translatedConstants';

/**
 * AttackPathCarousel Component
 * Displays attack paths grouped by scenario (Source Risk -> Dreaded Event)
 * Allows drill-down selection for a cleaner, modern UX.
 * 
 * @param {Object} props - Component props
 */
const AttackPathCarousel = ({
    attackPaths,
    selectedPath,
    onSelect,
    isLoading,
    timestamps,
    formatTimestamp,
    ctiStatus = {},
    aiComponents = {},
    onAIComponentsChange,
    onToggleRetention,
    businessAssetsMap = {} // Map of businessValueId -> support assets array
}) => {
    const { t } = useTranslation();
    const scrollRef = useRef(null);

    // State for navigation and view mode
    const [viewMode, setViewMode] = useState('groups'); // 'groups' | 'paths'
    const [activeGroup, setActiveGroup] = useState(null);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(true);

    // Group attack paths by Source Risk + Dreaded Event
    const groups = useMemo(() => {
        const g = {};
        attackPaths.forEach(path => {
            // Create a unique key for grouping
            const key = `${path.sourceRiskName}||${path.dreadedEventName}`;

            if (!g[key]) {
                // Infer strategic scenario reference
                let stratRef = "SC";
                if (path.strategicScenarioReference) {
                    stratRef = path.strategicScenarioReference;
                } else if (path.sourceRiskName && path.sourceRiskName.includes("-")) {
                    const firstWord = path.sourceRiskName.split(" ")[0];
                    if (/^(S\d+|CAD\d+|CA\d+)$/i.test(firstWord)) {
                        stratRef = firstWord;
                    }
                }

                g[key] = {
                    id: key,
                    sourceRiskName: path.sourceRiskName,
                    dreadedEventName: path.dreadedEventName,
                    businessValueName: path.businessValueName,
                    strategicReference: stratRef,
                    severity: path.dreadedEventSeverity || path.severity || 'moderate',
                    paths: [],
                    ctiCount: 0,
                    stakeholders: new Set()
                };
            }

            g[key].paths.push(path);
            if (ctiStatus[path.id]?.exists) g[key].ctiCount++;

            // Aggegate stakeholders
            if (path.stakeholders) {
                path.stakeholders.forEach(s => g[key].stakeholders.add(s.name));
            }
        });

        return Object.values(g).map(group => ({
            ...group,
            stakeholders: Array.from(group.stakeholders).map((name, i) => ({ id: i, name }))
        }));
    }, [attackPaths, ctiStatus]);

    // Handle initial selection
    useEffect(() => {
        if (selectedPath && viewMode === 'groups' && !activeGroup) {
            const group = groups.find(g => g.paths.some(p => p.id === selectedPath.id));
            if (group) {
                setActiveGroup(group);
                if (selectedPath.id) {
                    setViewMode('paths');
                }
            }
        }
    }, [selectedPath, groups]);

    // Reset scroll when data changes
    useEffect(() => {
        setCurrentIndex(0);
        updateScrollState();
    }, [viewMode, activeGroup, attackPaths]);

    // Listen to scroll events and DOM mutations (for animations)
    useEffect(() => {
        const handleScroll = () => updateScrollState();
        const handleResize = () => updateScrollState();

        // MutationObserver to detect when Framer Motion adds/removes nodes
        // This ensures arrows update correctly after animations complete
        let mutationObserver = null;

        if (scrollRef.current) {
            scrollRef.current.addEventListener('scroll', handleScroll);
            window.addEventListener('resize', handleResize);

            mutationObserver = new MutationObserver(() => {
                updateScrollState();
            });

            mutationObserver.observe(scrollRef.current, {
                childList: true,
                subtree: true,
                attributes: true // In case styles/classes change affecting width
            });

            // Initial check
            updateScrollState();

            // Safety timeout for complex animations
            setTimeout(updateScrollState, 500);
        }

        return () => {
            if (scrollRef.current) {
                scrollRef.current.removeEventListener('scroll', handleScroll);
            }
            if (mutationObserver) {
                mutationObserver.disconnect();
            }
            window.removeEventListener('resize', handleResize);
        };
    }, [viewMode, activeGroup]);

    const updateScrollState = () => {
        if (scrollRef.current) {
            const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
            setCanScrollLeft(scrollLeft > 0);
            setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
        }
    };

    const scroll = (direction) => {
        if (scrollRef.current) {
            const cardWidth = scrollRef.current.children[0]?.offsetWidth || 300;
            const gap = 16;
            const offset = direction === 'left' ? -(cardWidth + gap) : (cardWidth + gap);

            scrollRef.current.scrollBy({ left: offset, behavior: 'smooth' });

            // Update index approximation
            if (direction === 'left') setCurrentIndex(Math.max(0, currentIndex - 1));
            else setCurrentIndex(currentIndex + 1);
        }
    };

    // Use consistent styling from DreadedEvents.js
    const getSeverityStyles = (severity) => {
        const s = (severity || 'moderate').toLowerCase();

        // 5 - Catastrophic
        if (s.includes('catastroph') || s === 'g5' || s.includes('very high') || s.includes('très élevé')) return {
            bg: 'bg-white', text: 'text-slate-700', border: 'border-slate-200 hover:border-slate-400',
            icon: AlertCircle, line: 'bg-slate-900',
            titleText: 'text-slate-900', subText: 'text-slate-500',
            innerBg: 'bg-slate-50 border border-slate-200',
            footerBg: 'border-slate-100 bg-white',
            pillBg: 'bg-slate-100 text-slate-700',
            iconColor: 'text-slate-600',
            btnBg: 'bg-slate-50 group-hover:bg-slate-200',
            btnIcon: 'text-slate-500',
            badge: 'bg-slate-100 text-slate-800 border-slate-200'
        };
        // 4 - Critical
        if (s.includes('critique') || s.includes('critiqu') || s.includes('critical') || s === 'g4') return {
            bg: 'bg-white', text: 'text-red-700', border: 'border-red-200 hover:border-red-400',
            icon: AlertCircle, line: 'bg-red-500',
            titleText: 'text-gray-900', subText: 'text-gray-500',
            innerBg: 'bg-red-50/30 border border-red-100',
            footerBg: 'border-gray-100 bg-white',
            pillBg: 'bg-red-50 text-red-700',
            iconColor: 'text-red-500',
            btnBg: 'bg-red-50 group-hover:bg-red-100',
            btnIcon: 'text-red-500',
            badge: 'bg-red-50 text-red-700 border-red-200'
        };
        // 3 - Major
        if (s.includes('maj') || s.includes('high') || s.includes('élevé') || s === 'g3') return {
            bg: 'bg-white', text: 'text-orange-700', border: 'border-orange-200 hover:border-orange-400',
            icon: AlertCircle, line: 'bg-orange-500',
            titleText: 'text-gray-900', subText: 'text-gray-500',
            innerBg: 'bg-orange-50/30 border border-orange-100',
            footerBg: 'border-gray-100 bg-white',
            pillBg: 'bg-orange-50 text-orange-700',
            iconColor: 'text-orange-500',
            btnBg: 'bg-orange-50 group-hover:bg-orange-100',
            btnIcon: 'text-orange-500',
            badge: 'bg-orange-50 text-orange-700 border-orange-200'
        };
        // 2 - Moderate
        if (s.includes('modera') || s.includes('modéré') || s.includes('medium') || s.includes('moyen') || s === 'g2') return {
            bg: 'bg-white', text: 'text-amber-700', border: 'border-amber-200 hover:border-amber-400',
            icon: AlertCircle, line: 'bg-amber-500',
            titleText: 'text-gray-900', subText: 'text-gray-500',
            innerBg: 'bg-amber-50/30 border border-amber-100',
            footerBg: 'border-gray-100 bg-white',
            pillBg: 'bg-amber-50 text-amber-700',
            iconColor: 'text-amber-500',
            btnBg: 'bg-amber-50 group-hover:bg-amber-100',
            btnIcon: 'text-amber-500',
            badge: 'bg-amber-50 text-amber-700 border-amber-200'
        };

        // 1 - Minor / Default
        return {
            bg: 'bg-white', text: 'text-emerald-700', border: 'border-emerald-200 hover:border-emerald-400',
            icon: CheckCircle, line: 'bg-emerald-500',
            titleText: 'text-gray-900', subText: 'text-gray-500',
            innerBg: 'bg-emerald-50/30 border border-emerald-100',
            footerBg: 'border-gray-100 bg-white',
            pillBg: 'bg-emerald-50 text-emerald-700',
            iconColor: 'text-emerald-500',
            btnBg: 'bg-emerald-50 group-hover:bg-emerald-100',
            btnIcon: 'text-emerald-500',
            badge: 'bg-emerald-50 text-emerald-700 border-emerald-200'
        };
    };

    if (isLoading) return <div className="text-center py-20"><Loader2 className="h-10 w-10 animate-spin mx-auto text-indigo-600" /></div>;

    if (attackPaths.length === 0) return (
        <div className="flex flex-col items-center justify-center py-20 text-center bg-gray-50 rounded-2xl border border-dashed border-gray-300">
            <Layers className="h-12 w-12 text-gray-400 mb-3" />
            <p className="text-gray-500 font-medium">{t('workshop4.attackPathCarousel.noAttackPaths')}</p>
        </div>
    );

    // ponytail: derive paths from live attackPaths so toggle updates reflect immediately
    const itemsToDisplay = viewMode === 'groups' ? groups : (activeGroup
        ? attackPaths.filter(p => `${p.sourceRiskName}||${p.dreadedEventName}` === activeGroup.id)
        : []);

    return (
        <div className="relative w-full">
            {/* Header / Breadcrumbs */}
            <div className="flex items-center justify-between mb-6 px-1">
                <div className="flex items-center space-x-2">
                    {viewMode === 'paths' ? (
                        <button
                            onClick={() => {
                                setViewMode('groups');
                                setActiveGroup(null);
                                onSelect(null); // Clear selected path to hide steps 2 & 3
                            }}
                            className="group flex items-center px-3 py-1.5 bg-white border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50 hover:text-indigo-600 transition-all font-medium text-sm shadow-sm"
                        >
                            <CornerUpLeft className="h-4 w-4 mr-1.5 group-hover:-translate-x-0.5 transition-transform" />
                            {t('workshop4.buttons.backToScenarios', 'Scénarios')}
                        </button>
                    ) : (
                        <div className="flex items-center space-x-2 text-gray-800">
                            <LayoutGrid className="h-5 w-5 text-indigo-600" />
                            <h3 className="text-lg font-bold">
                                {t('workshop4.titles.scenariosOverview', 'Scénarios Opérationnels')}
                                <span className="ml-2 px-2.5 py-0.5 bg-indigo-100 text-indigo-700 text-xs rounded-full font-bold">
                                    {groups.length}
                                </span>
                            </h3>
                        </div>
                    )}

                    {viewMode === 'paths' && activeGroup && (
                        <>
                            <ArrowRight className="h-4 w-4 text-gray-300" />
                            <div className="flex items-center text-gray-900 font-semibold bg-gray-100 px-3 py-1 bg-opacity-50 rounded-lg">
                                <span className="truncate max-w-[200px]">{activeGroup.dreadedEventName}</span>
                                <span className="ml-2 px-2 py-0.5 bg-indigo-100 text-indigo-700 text-xs rounded-full">
                                    {activeGroup.paths.length} {t('workshop4.labels.paths', 'chemins')}
                                </span>
                            </div>
                        </>
                    )}
                </div>

                {/* Navigation Controls */}
                <div className="flex items-center space-x-2">
                    <button
                        onClick={() => scroll('left')}
                        disabled={!canScrollLeft}
                        className={`p-2 rounded-full transition-all duration-200 border ${canScrollLeft
                            ? 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50 hover:text-indigo-600 shadow-sm'
                            : 'bg-gray-50 border-gray-100 text-gray-300 cursor-not-allowed'
                            }`}
                    >
                        <ChevronLeft className="h-5 w-5" />
                    </button>
                    <button
                        onClick={() => scroll('right')}
                        disabled={!canScrollRight}
                        className={`p-2 rounded-full transition-all duration-200 border ${canScrollRight
                            ? 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50 hover:text-indigo-600 shadow-sm'
                            : 'bg-gray-50 border-gray-100 text-gray-300 cursor-not-allowed'
                            }`}
                    >
                        <ChevronRight className="h-5 w-5" />
                    </button>
                </div>
            </div>

            {/* Carousel Content */}
            <div
                ref={scrollRef}
                className="flex overflow-x-auto snap-x snap-mandatory scroll-smooth pb-8 pt-4 px-1 -mx-1 space-x-6 show-scrollbar-on-hover"
                style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
                <AnimatePresence mode='wait'>
                    {itemsToDisplay.map((item, index) => {
                        const styles = getSeverityStyles(item.severity);
                        const severityLabel = viewMode === 'groups' ? (getTranslatedSeverityLabel(item.severity) || item.severity) : '';

                        return (
                            <motion.div
                                key={viewMode === 'groups' ? item.id : item.id}
                                initial={{ opacity: 0, scale: 0.95, y: 10 }}
                                animate={{ opacity: 1, scale: 1, y: 0 }}
                                exit={{ opacity: 0, scale: 0.95 }}
                                transition={{ duration: 0.3, delay: index * 0.05 }}
                                className="flex-shrink-0 w-full sm:w-[320px] md:w-[350px] snap-center"
                            >
                                {viewMode === 'groups' ? (
                                    // === GROUP CARD (SCENARIO) ===
                                    <div
                                        onClick={() => {
                                            setActiveGroup(item);
                                            setViewMode('paths');
                                        }}
                                        className={`group relative h-full ${styles.bg} rounded-2xl border ${styles.border} shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer overflow-hidden flex flex-col`}
                                    >
                                        {/* Gradient Top Line based on severity */}
                                        <div className={`h-1.5 w-full ${styles.line}`}></div>

                                        <div className="p-5 flex flex-col h-full">
                                            {/* Header: Strategic Ref & Severity */}
                                            <div className="flex justify-between items-start mb-4">
                                                <div className="flex flex-col">
                                                    <span className={`text-[10px] uppercase font-bold tracking-wider ${styles.subText}`}>Scénario Stratégique</span>
                                                    <span className={`font-bold text-base truncate max-w-[150px] ${styles.titleText}`} title={item.strategicReference}>
                                                        {item.strategicReference}
                                                    </span>
                                                </div>

                                                <div className={`px-2.5 py-1 rounded-full text-xs font-bold border flex items-center gap-1.5 ${styles.badge}`}>
                                                    <styles.icon className="h-3 w-3" />
                                                    <span>{severityLabel}</span>
                                                </div>
                                            </div>

                                            {/* Content: Dreaded Event */}
                                            <div className="mb-4 flex-grow">
                                                <span className={`text-[10px] uppercase font-bold tracking-wider mb-1 block ${styles.subText}`}>Événement Redouté</span>
                                                <h4 className={`text-lg font-bold mb-3 leading-snug group-hover:opacity-80 transition-opacity line-clamp-2 ${styles.titleText}`} title={item.dreadedEventName}>
                                                    {item.dreadedEventName}
                                                </h4>

                                                {/* Source Risk Context */}
                                                <div className={`p-3 rounded-lg ${styles.innerBg}`}>
                                                    <span className={`text-[10px] uppercase font-bold tracking-wider block mb-0.5 ${styles.subText}`}>Source de Risque</span>
                                                    <p className={`text-xs font-medium line-clamp-2 ${styles.titleText}`}>
                                                        {item.sourceRiskName}
                                                    </p>
                                                </div>
                                            </div>

                                            {/* Footer: Stats & Action */}
                                            <div className={`mt-auto pt-4 border-t flex items-center justify-between ${styles.footerBg}`}>
                                                <div className="flex items-center gap-3">
                                                    <div className={`flex items-center text-xs font-medium px-2 py-1 rounded-md ${styles.pillBg}`}>
                                                        <Layers className={`h-3.5 w-3.5 mr-1.5 ${styles.iconColor}`} />
                                                        {item.paths.length} Chemins
                                                    </div>
                                                    {item.ctiCount > 0 && (
                                                        <div className={`flex items-center text-xs font-bold px-2 py-1 rounded-md border ${item.ctiCount === item.paths.length
                                                            ? 'bg-green-100 text-green-700 border-green-200'
                                                            : 'bg-blue-50 text-blue-700 border-blue-200'
                                                            }`}>
                                                            <Shield className="h-3.5 w-3.5 mr-1.5" />
                                                            {item.ctiCount}/{item.paths.length} {t('workshop4.labels.analyzed', 'Analysés')}
                                                        </div>
                                                    )}
                                                </div>

                                                <div className={`h-8 w-8 rounded-full flex items-center justify-center transition-colors ${styles.btnBg}`}>
                                                    <ArrowRight className={`h-4 w-4 transition-colors ${styles.btnIcon}`} />
                                                </div>
                                            </div>
                                        </div>

                                        {/* Visual Stack Effect */}
                                        <div className="absolute top-1 right-1 -z-10 bg-gray-200 rounded-2xl w-full h-full transform translate-x-2 translate-y-2 opacity-50"></div>
                                    </div>
                                ) : (() => {
                                    // Check if attack path has support assets via businessAssetsMap
                                    const pathBusinessAssets = businessAssetsMap[item.businessValueId] || [];
                                    const hasNoSupportAssets = pathBusinessAssets.length === 0;

                                    return (
                                        // === ATTACK PATH CARD (Individual) ===
                                        <div
                                            onClick={hasNoSupportAssets ? undefined : () => onSelect(item)}
                                            className={`group relative h-full min-h-[220px] flex flex-col rounded-2xl p-5 mt-3 border transition-all duration-300 transform ${hasNoSupportAssets
                                                ? 'cursor-not-allowed hover:scale-[1.01] hover:shadow-md border-gray-300 bg-gray-50/50'
                                                : `cursor-pointer hover:scale-[1.02] ${selectedPath?.id === item.id
                                                    ? 'border-indigo-500 bg-gradient-to-br from-indigo-50/80 to-blue-50/80 shadow-xl ring-2 ring-indigo-500 ring-offset-2'
                                                    : ctiStatus[item.id]?.exists
                                                        ? 'border-green-400 bg-green-50/20 hover:border-green-500 hover:shadow-lg ring-1 ring-green-400/30'
                                                        : 'border-gray-200 bg-white hover:border-indigo-300 hover:shadow-lg'
                                                }`
                                                }`}
                                        >
                                            {/* No Support Assets Badge */}
                                            {hasNoSupportAssets && (
                                                <div className="absolute -top-3 left-4 bg-amber-500 text-white rounded-full px-3 py-1.5 flex items-center shadow-lg z-20">
                                                    <AlertCircle className="h-3.5 w-3.5 mr-1.5" />
                                                    <span className="text-[11px] uppercase font-bold tracking-wide">{t('workshop4.attackPathCarousel.noSupportAssets', 'Sans bien support')}</span>
                                                </div>
                                            )}


                                            {/* All Assets Analyzed Badge (Yellow = All, Green = Partial) */}
                                            {ctiStatus[item.id]?.exists && !hasNoSupportAssets && (() => {
                                                const analyzedAssetsCount = ctiStatus[item.id]?.assetsCount || 0;
                                                const totalAssetsCount = pathBusinessAssets.length;
                                                const allAnalyzed = analyzedAssetsCount >= totalAssetsCount && totalAssetsCount > 0;

                                                return (
                                                    <div className={`absolute -top-3 left-4 ${allAnalyzed ? 'bg-yellow-500' : 'bg-green-500'} text-white rounded-full px-3 py-1.5 flex items-center shadow-lg z-20`}>
                                                        <Target className="h-3.5 w-3.5 mr-1.5" />
                                                        <span className="text-[11px] uppercase font-bold tracking-wide">
                                                            {allAnalyzed ? t('workshop4.attackPathCarousel.allAssetsAnalyzed', 'Tous analysés') : `${analyzedAssetsCount}/${totalAssetsCount}`}
                                                        </span>
                                                    </div>
                                                );
                                            })()}


                                            {/* Reference & Header */}
                                            <div className="flex items-center gap-2 mb-3">
                                                <span className={`text-xs font-bold px-2 py-1 rounded-md shadow-sm ${selectedPath?.id === item.id ? 'bg-indigo-600 text-white' : 'bg-gray-800 text-white'
                                                    }`}>
                                                    {item.referenceCode || `Var ${index + 1}`}
                                                </span>
                                                {selectedPath?.id === item.id && (
                                                    <CheckCircle className="h-4 w-4 text-indigo-600" />
                                                )}
                                            </div>

                                            {/* Main Title */}
                                            <h3 className="text-base font-bold text-gray-800 mb-3 leading-snug line-clamp-2 group-hover:text-indigo-700 transition-colors">
                                                {item.objectifVise || "Chemin d'attaque"}
                                            </h3>

                                            {/* Details Grid */}
                                            <div className="space-y-2 mb-4 bg-white/50 rounded-lg p-2 border border-gray-100/50">
                                                <div className="flex items-start gap-2 text-xs">
                                                    <div className="min-w-[4px] h-[4px] rounded-full bg-orange-400 mt-1.5"></div>
                                                    <div>
                                                        <span className="text-gray-400 font-medium block text-[10px] uppercase">Value</span>
                                                        <span className="text-gray-700 font-medium line-clamp-1">{item.businessValueName}</span>
                                                    </div>
                                                </div>
                                                {/* Stakeholder / Partie Prenante */}
                                                {item.stakeholders && item.stakeholders.length > 0 && (
                                                    <div className="flex items-start gap-2 text-xs">
                                                        <div className="min-w-[4px] h-[4px] rounded-full bg-purple-400 mt-1.5"></div>
                                                        <div>
                                                            <span className="text-gray-400 font-medium block text-[10px] uppercase">{t('workshop4.labels.stakeholder', 'Partie Prenante')}</span>
                                                            <span className="text-gray-700 font-medium line-clamp-1">
                                                                {item.stakeholders.map(s => typeof s === 'object' ? (s.name || s.label) : s).join(', ')}
                                                            </span>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>

                                            {/* Footer / Controls */}
                                            <div className="mt-auto pt-3 border-t border-gray-100/80">
                                                <label className={`flex items-center justify-between p-2 rounded-lg transition-colors ${hasNoSupportAssets
                                                    ? 'cursor-not-allowed opacity-50'
                                                    : `cursor-pointer ${aiComponents[item.id] ? 'bg-blue-50 border border-blue-100' : 'hover:bg-gray-50'}`
                                                    }`}>
                                                    <div className="flex items-center gap-2">
                                                        <input
                                                            type="checkbox"
                                                            checked={aiComponents[item.id] || false}
                                                            disabled={hasNoSupportAssets}
                                                            onChange={(e) => {
                                                                e.stopPropagation();
                                                                if (!hasNoSupportAssets) {
                                                                    onAIComponentsChange && onAIComponentsChange(item.id, e.target.checked);
                                                                }
                                                            }}
                                                            className={`w-4 h-4 rounded border-gray-300 focus:ring-blue-500 ${hasNoSupportAssets ? 'opacity-50 cursor-not-allowed' : 'text-blue-600'}`}
                                                        />
                                                        <span className="text-xs font-semibold text-gray-600 flex items-center">
                                                            <Bot className="h-3 w-3 mr-1.5 text-blue-500" />
                                                            AI Assist
                                                        </span>
                                                    </div>
                                                {aiComponents[item.id] && !hasNoSupportAssets && <Zap className="h-3 w-3 text-amber-500 fill-amber-500 animate-pulse" />}
                                            </label>

                                            {/* Retention toggle */}
                                            <div className="mt-2 flex items-center justify-between px-2">
                                                <div className="flex items-center gap-1.5">
                                                    <Switch
                                                        checked={item.retained !== false}
                                                        onChange={(checked) => { onToggleRetention?.(item.id, checked); }}
                                                        className={`${item.retained !== false ? 'bg-emerald-500' : 'bg-slate-300'} relative inline-flex h-4 w-7 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-300 focus:ring-offset-1`}
                                                    >
                                                        <span className={`${item.retained !== false ? 'translate-x-3.5' : 'translate-x-[2px]'} inline-block h-3 w-3 transform rounded-full bg-white transition-transform`} />
                                                    </Switch>
                                                    <span className={`text-[10px] uppercase font-semibold ${item.retained !== false ? 'text-emerald-600' : 'text-slate-400'}`}>
                                                        {item.retained !== false ? t('workshop3.activity3.retainedLabel') : t('workshop3.activity3.retainedOffLabel')}
                                                    </span>
                                                </div>
                                            </div>

                                            {ctiStatus[item.id]?.createdAt && (
                                                    <div className="mt-2 flex items-center justify-end text-[10px] text-gray-400">
                                                        <Clock className="h-3 w-3 mr-1" />
                                                        {formatTimestamp ? formatTimestamp(ctiStatus[item.id].createdAt) : ''}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )
                                })()}
                            </motion.div>
                        );
                    })}
                </AnimatePresence>

                {/* Spacer for right padding */}
                <div className="w-4 flex-shrink-0"></div>
            </div>
        </div>
    );
};

export default AttackPathCarousel;